import {
  faArrowUpRightFromSquare,
  faChevronDown,
  faCircleCheck,
  faCircleXmark,
  faClock
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useContext, useId, useState } from 'react';
import { Link } from 'react-router-dom';

import { encounterChecklistChannelName } from '@/channels/utils/channelNames';
import ActiveTabLink from '@/components/ActiveTabLink';
import DrugScreenWarningModal from '@/components/DrugScreenWarningModal';
import AppContext from '@/contexts/appContext';
import { useChannelData } from '@/hooks/useChannel';
import useEncounterChecklistPresence from '@/hooks/useEncounterChecklistPresence';
import { useAttendanceChecklist } from '@/hooks/useEncounters';
import useRecordDrugScreen from '@/hooks/useRecordDrugScreen';
import type { EncounterChecklistItem } from '@/types/encounter';
import { Pill, Spinner, Tooltip, Typography } from '@/ui';
import { cn } from '@/ui/lib/utils';
import encounterChecklistItemLink, {
  type EncounterChecklistItemLink
} from '@/utils/encounterChecklistItemLink';

import './styles.css';
import type { Props } from './types';

const pluralize = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;

// What each item's state means, shown on its status icon
const STATE_TEXT: Record<string, { done: string; todo: string }> = {
  mark_attendance: { done: 'Attendance recorded', todo: 'Attendance not recorded' },
  complete_charting_note: { done: 'Charting note complete', todo: 'Charting note incomplete' },
  enroll_member: { done: 'Member enrolled', todo: 'Member not enrolled' },
  record_drug_screen: { done: 'Drug screen recorded', todo: 'Drug screen not recorded' },
  missing_information: { done: 'Nothing to review', todo: 'Items need review' }
};

const statusFor = (item: EncounterChecklistItem) => {
  const text = STATE_TEXT[item.key];
  if (item.is_complete === true)
    return {
      label: text?.done ?? 'Done',
      icon: faCircleCheck,
      className: 'text-uniform-green-600'
    };
  if (item.is_complete === false)
    return {
      label: text?.todo ?? 'To do',
      icon: faCircleXmark,
      className: 'text-uniform-red-600'
    };
  return {
    label: 'Waiting for attendance to be marked',
    icon: faClock,
    className: 'text-uniform-yellow-600 dark:text-uniform-yellow-500'
  };
};

// Anything to explain about an item sits behind one pill that expands into a list
const detailsFor = (item: EncounterChecklistItem) => {
  if (item.errors.length)
    return {
      pill: pluralize(item.errors.length, 'error'),
      color: 'destructive' as const,
      lines: item.errors
    };
  return null;
};

const StatusIcon = ({ item }: { item: EncounterChecklistItem }) => {
  const status = statusFor(item);
  return (
    <Tooltip content={status.label}>
      <span aria-label={status.label} role="img">
        <FontAwesomeIcon aria-hidden className={status.className} icon={status.icon} />
      </span>
    </Tooltip>
  );
};

const outlinkClassName = 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200';
const outlinkIcon = (
  <FontAwesomeIcon aria-hidden className="text-xs" icon={faArrowUpRightFromSquare} />
);

const ItemLink = ({ link, label }: { link: EncounterChecklistItemLink; label: string }) => {
  const className = outlinkClassName;
  const icon = outlinkIcon;

  return link.external ? (
    <ActiveTabLink aria-label={`Open ${label}`} className={className} href={link.href}>
      {icon}
    </ActiveTabLink>
  ) : (
    // The app doesn't reset scroll between routes, so the page would open at this one's offset
    <Link
      aria-label={`Open ${label}`}
      className={className}
      onClick={() => window.scrollTo(0, 0)}
      to={link.href}>
      {icon}
    </Link>
  );
};

/**
 * What a provider still has to do for this attendance. An item's errors stay
 * behind a pill so opening the checklist isn't a wall of text.
 */
const EncounterChecklistCard = ({ encounter, encounterId, personId }: Props) => {
  const { host } = useContext(AppContext);
  const { data: items = [], isLoading, isError, refetch } = useAttendanceChecklist({ encounterId });
  useEncounterChecklistPresence(encounterId);

  const queryClient = useQueryClient();

  // The billing item was regenerated; refetch both cards' data without regenerating again.
  useChannelData({
    channelId: encounterChecklistChannelName(personId),
    channelName: 'encounter checklist',
    eventName: 'encounter_checklist_changed',
    messageCallback: () => {
      refetch();
      queryClient.invalidateQueries({ queryKey: ['getEncounterById', encounterId] });
    }
  });
  const idPrefix = useId();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const toggle = useCallback(
    (key: string) => setExpanded((current) => ({ ...current, [key]: !current[key] })),
    []
  );
  const { canRecordDrugScreen, recordDrugScreen, warningModalProps } =
    useRecordDrugScreen(encounter);

  return (
    <>
      {isLoading && <Spinner className="mt-2" size="sm" />}

      {isError && (
        <Typography className="mt-2" muted variant="caption">
          Could not load the checklist.
        </Typography>
      )}

      <ul className="mt-2 flex flex-col gap-2">
        {items.map((item) => {
          const labelId = `${idPrefix}-${item.key}`;
          const detailsId = `${labelId}-details`;
          const isExpanded = !!expanded[item.key];
          const details = detailsFor(item);
          const link = encounterChecklistItemLink({ itemKey: item.key, encounter, host });
          const summary = (
            <>
              <StatusIcon item={item} />
              <Typography
                className={cn('min-w-0', item.is_complete === false && 'text-uniform-red-600')}
                id={labelId}
                variant="body">
                {item.label}
              </Typography>
              {details && (
                <span className="flex shrink-0 items-center gap-1.5">
                  <Pill className="whitespace-nowrap" color={details.color} size="sm">
                    {details.pill}
                  </Pill>
                  <FontAwesomeIcon
                    aria-hidden
                    className={cn(
                      'h-3 w-3 text-uniform-gray-500 transition-transform',
                      !isExpanded && '-rotate-90'
                    )}
                    icon={faChevronDown}
                  />
                </span>
              )}
            </>
          );

          return (
            <li aria-labelledby={labelId} key={item.key}>
              <div className="flex items-center gap-2">
                {details ? (
                  <button
                    aria-controls={detailsId}
                    aria-expanded={isExpanded}
                    aria-label={`${details.pill} for ${item.label}`}
                    className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 rounded text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                    onClick={() => toggle(item.key)}
                    type="button">
                    {summary}
                  </button>
                ) : (
                  <div className="flex min-w-0 flex-1 items-center gap-2">{summary}</div>
                )}

                {item.key === 'record_drug_screen' && !link
                  ? canRecordDrugScreen && (
                      <button
                        aria-label={`Open ${item.label}`}
                        className={outlinkClassName}
                        onClick={recordDrugScreen}
                        type="button">
                        {outlinkIcon}
                      </button>
                    )
                  : link && <ItemLink label={item.label} link={link} />}
              </div>

              {details && isExpanded && (
                // Indented under the item, lined up with its label
                <ul className="ml-6 mt-1 flex list-disc flex-col gap-1 pl-4" id={detailsId}>
                  {details.lines.map((line) => (
                    <li className="text-slate-500" key={line.key}>
                      <Typography as="span" muted variant="caption">
                        {line.message}
                      </Typography>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>

      {warningModalProps && <DrugScreenWarningModal {...warningModalProps} />}
    </>
  );
};

export default React.memo(EncounterChecklistCard);
